import * as FiIcons from 'react-icons/fi';
import {
  IconButton as IconButtonBase,
  Flex as FlexBase,
  HStack as HStackBase,
  Link as ChakraLinkBase,
} from '@chakra-ui/react';
import { entradaDoPedido } from '../helpers/entradaPedido';

// Cast Chakra components to bypass TS2590 ("union type too complex").
const IconButton = IconButtonBase as React.ComponentType<any>;
const Flex = FlexBase as React.ComponentType<any>;
const HStack = HStackBase as React.ComponentType<any>;
const ChakraLink = ChakraLinkBase as React.ComponentType<any>;

// Situações em que o pedido não vale mais: excluir no Protheus grava
// EXCLUIDO_ERP, cancelar grava CANCELADO_ERP (ver STATUS_BAIXA_ERP no webhook
// do ESF-API). A coluna imprimia o valor cru no meio dos outros, então um
// pedido apagado no TOTVS passava batido na leitura da tabela.
const SITUACOES_BAIXADAS = ['EXCLUIDO_ERP', 'CANCELADO_ERP', 'EXCLUIDO', 'CANCELADO'];

// Rótulo legível no lugar do código do ERP.
const ROTULO_SITUACAO: Record<string, string> = {
  EXCLUIDO_ERP: 'Excluído no TOTVS',
  CANCELADO_ERP: 'Cancelado no TOTVS',
  NOTA_CANCELADA: 'Nota cancelada',
  ERRO_INTEGRACAO: 'Erro de integração',
  EM_ANALISE: 'Em análise',
  AGUARDANDO_INTEGRACAO: 'Aguardando integração',
};

// Pedido com esses problemas continua mostrando o problema, mesmo entregue:
// é o que alguém precisa resolver.
const SITUACOES_QUE_PREVALECEM = [...SITUACOES_BAIXADAS, 'NOTA_CANCELADA', 'ERRO_INTEGRACAO'];

/**
 * O que a coluna Situação mostra.
 *
 * A `situacao` do banco acompanha o ERP e para em FATURADO: a entrega não
 * existe no TOTVS. Quando a entrega do pedido está ENTREGUE — o canhoto foi
 * anexado, pelo motorista no app ou à mão em Canhotos — a coluna passa a
 * mostrar ENTREGUE. `status_entrega` é preenchido em ListOrder a partir das
 * entregas (ver listarStatusEntregaPorPedido).
 */
export function situacaoDoPedido(pedido: any): { rotulo: string; baixado: boolean; entregue: boolean } {
  const situacao = String(pedido?.situacao || '').trim().toUpperCase();
  const baixado = SITUACOES_BAIXADAS.includes(situacao);
  if (pedido?.status_entrega === 'ENTREGUE' && !SITUACOES_QUE_PREVALECEM.includes(situacao)) {
    return { rotulo: 'ENTREGUE', baixado: false, entregue: true };
  }
  return { rotulo: ROTULO_SITUACAO[situacao] || pedido?.situacao || '—', baixado, entregue: false };
}

export function getColumn(
  deleteFunction: (e: any, id: string) => void,
  editPath: string,
  showModalFuntion: (index: number) => void,
  approveFunction: (id: string) => void,
  isGerente: boolean
) {
  const columns: any[] = [
    {
      // Mesmo código que o app mostra (primeiros 8 caracteres do pedido_id),
      // e o accessor devolve esse texto para o filtro da coluna achar o pedido.
      Header: 'Código',
      id: 'codigo',
      accessor: (row: any) => String(row.pedido_id ?? '').substring(0, 8),
      Cell: ({ value }: any) => <span>{value}</span>,
    },
    {
      Header: 'Nº Pedido',
      accessor: 'pedido_erp',
      // Número do pedido no TOTVS (pedido_erp). Vazio enquanto não integrou.
      Cell: ({ row }: any) => <span>{row.original.pedido_erp || '—'}</span>,
    },
    {
      Header: 'Cliente',
      accessor: 'clientes.razao_social',
    },
    {
      // Preenchido em ListOrder a partir de GET /empresas (a lista junta os
      // pedidos de todas as filiais, então sem isto não dava para saber de
      // qual empresa era cada pedido).
      Header: 'Filial',
      accessor: 'filial_nome',
      Cell: ({ row }: any) => <span>{row.original.filial_nome || '—'}</span>,
    },
    {
      Header: 'Vendedor',
      accessor: 'colaboradores.nome',
    },
    {
      Header: 'Data de emissão',
      id: 'data_emissao',
      // Ordena pelo momento de entrada do pedido (menor = mais antigo): a
      // string ISO não serve, porque o pedido do Protheus grava só o dia e
      // traz a hora à parte, em hora_emissao.
      accessor: (row: any) =>
        entradaDoPedido(row.data_emissao, row.hora_emissao)?.momento.toMillis() ?? 0,
      sortType: 'basic',
      // Dia e hora em que o pedido entrou no Essencial, no horário local.
      // Pedido antigo do Protheus, sem hora gravada, mostra só o dia.
      Cell: ({ row }: any) => {
        const entrada = entradaDoPedido(
          row.original.data_emissao,
          row.original.hora_emissao
        );
        if (!entrada) return '-';
        // Sem quebra: a hora fica na mesma linha da data.
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {entrada.temHora
              ? entrada.momento.toFormat("dd/MM/yyyy 'às' HH:mm")
              : entrada.momento.toFormat('dd/MM/yyyy')}
          </span>
        );
      },
    },
    {
      Header: 'Margem Pedido',
      accessor: 'margemPedido',
      Cell: ({ row }: any) => (
        <span>{(row.original.margem_pedido * 100).toFixed(2)}%</span>
      ),
    },
    {
      Header: 'Tipo Pedido',
      accessor: 'tipo_pedido',
      // O tipo correto é o que o app realmente enviou em payload_enviado.TIPO_PEDIDO.
      // A coluna tipo_pedido vem do ERP e está divergente em vários pedidos (ex.:
      // CONSIGNACAO/URGENCIA gravados como VENDA), então o payload tem prioridade;
      // a coluna é só fallback (pedidos importados direto do TOTVS, sem payload).
      Cell: ({ row }: any) => {
        let payload = row.original.payload_enviado;
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch {
            payload = undefined;
          }
        }
        const tipoApp = payload?.TIPO_PEDIDO;
        return <span>{tipoApp || row.original.tipo_pedido || '—'}</span>;
      },
    },
    {
      Header: 'Situação',
      // O valor da coluna é o rótulo exibido: o filtro e a ordenação batem com
      // o que se lê (filtrar "ENTREGUE" acha o pedido com canhoto).
      id: 'situacao',
      accessor: (pedido: any) => situacaoDoPedido(pedido).rotulo,
      Cell: ({ row }: any) => {
        const { rotulo, baixado, entregue } = situacaoDoPedido(row.original);
        return (
          <span
            style={{
              color: baixado ? '#E53E3E' : entregue ? '#38A169' : undefined,
              fontWeight: baixado ? 700 : entregue ? 600 : undefined,
            }}
          >
            {rotulo}
          </span>
        );
      },
    },
    {
      Header: ' ',
      Cell: ({ row }: any) => (
        <Flex as="main" alignItems="center">
          <HStack spacing={2}>
            {/* <ChakraLink
              onClick={(e) => {
                showModalFuntion(Number(row.original.id));
              }}
            >
              <IconButton
                size="sm"
                aria-label="Informações"
                colorScheme="green"
                bg="green.500"
                _hover={{
                  bg: "green.700",
                }}
                icon={<FiIcons.FiInfo size={18} color="#eeeef2" />}
              />
            </ChakraLink> */}
            <ChakraLink href={`${editPath}/${row.original.pedido_id}`}>
              <IconButton
                size="sm"
                aria-label="Informações"
                colorScheme="green"
                bg="green.500"
                _hover={{
                  bg: 'green.700',
                }}
                icon={<FiIcons.FiInfo size={18} color="#eeeef2" />}
              />
            </ChakraLink>
            {/* Aprovação/reprovação agora só dentro do pedido (ListItensOrder),
                não mais pela lista. */}
            {/* <ChakraLink
              onClick={(e) => {
                deleteFunction(e, row.original.id);
              }}
              href="/"
            >
              <IconButton
                size="sm"
                aria-label="Editar"
                colorScheme="red"
                bg="red.500"
                _hover={{
                  bg: "red.700",
                }}
                icon={<FiIcons.FiXSquare size={18} color="#eeeef2" />}
              />
            </ChakraLink> */}
          </HStack>
        </Flex>
      ),
      disableSortBy: true,
      disableFilters: true,
    },
  ];

  return columns;
}
